/**
 * Export contract — G2.4.
 *
 *   "Every table, grid and chart in the product exports to CSV and XLSX through one
 *    shared export service. There is no screen from which a planner cannot get the
 *    numbers out."
 *
 * The types below encode G2.4's requirements structurally rather than trusting a
 * caller to honour them:
 *
 *   Fidelity          `ExportRequest.perimeter` carries the CURRENT filters, sort,
 *                     column set and drill level. It is not optional, so an export
 *                     of the unfiltered underlying query cannot be described.
 *   Numeric integrity `ExportCell` for a number is `{ raw: number }` — there is no
 *                     way to put a display string in a data column. The
 *                     workbook shows it in the user's locale through a NUMBER
 *                     FORMAT, never by writing text: the cell keeps the raw
 *                     value (DEC-104, `docs/adr/9041`).
 *   Provenance        `Provenance` has all nine fields and every one is required
 *                     (G2 test e) — STATED: a value, or a declared absence with its
 *                     reason (`ProvenanceAbsent`), never a silent blank and never an
 *                     invented value. `docs/adr/9007`.
 *   Currency          A monetary column must carry `currency`, so "explicit currency
 *                     column" is a type requirement.
 */

/**
 * A provenance field the export cannot state, declared rather than invented — `docs/adr/9007`.
 *
 * G2 test e requires all nine fields, and the platform does not publish several of them for
 * most reads (the plan version, the scenario, the hierarchy node, the generating principal).
 * Before this type the chapters split two ways: five wrote PLAUSIBLE CONSTANTS into every file
 * (`PV-2026-07-18`, `usr-000`, a 1970 timestamp, filters naming an option that was not
 * exported), and in-season left the fields blank — which `assertComplete` refused, so every
 * in-season XLSX export failed. A citation that will be believed is worse than a blank; a blank
 * says nothing about why. This says why: the sheet renders the reason, translated.
 */
export interface ProvenanceAbsent {
  readonly absent: true;
  /** ICU key for the sentence the sheet shows in place of the value. */
  readonly reasonKey: string;
}

/** A provenance value, or its declared absence. */
export type Stated<T> = T | ProvenanceAbsent;

export function isAbsent(value: unknown): value is ProvenanceAbsent {
  return typeof value === 'object' && value !== null && (value as { absent?: unknown }).absent === true;
}

/** design/09 §12 / G2.4 — the nine provenance fields, all required, each STATED. */
export interface Provenance {
  /** 1 — filters applied, as (field, operator, value) triples, human-readable. */
  readonly filters: Stated<readonly ProvenanceFilter[]>;
  /** 2 — hierarchy node the perimeter is anchored to. */
  readonly hierarchyNode: Stated<string>;
  /** 3 — time window, ISO-8601, inclusive start and end. */
  readonly timeWindow: Stated<{ readonly from: string; readonly to: string }>;
  /** 4 — scenario name. §8's scenario switcher shows this and the baseline. */
  readonly scenario: Stated<string>;
  /** 5 — plan version id (reqs/00 F3 business object, not a software version). */
  readonly planVersionId: Stated<string>;
  /** 6 — measure definitions used, cited as `metrics-vN` register entries (G7.5). */
  readonly measureDefinitions: readonly MeasureCitation[];
  /** 7 — generated-at timestamp, UTC, ISO-8601. */
  readonly generatedAt: string;
  /** 8 — generating user (principal id and display name). */
  readonly generatedBy: Stated<{ readonly principalId: string; readonly displayName: string }>;
  /** 9 — platform version, `vX.Y.Z`, matching the immutable release tag (G7.5). */
  readonly platformVersion: Stated<string>;
  /**
   * Columns in THIS file that are illustrative rather than measured, by their header, plus
   * an ICU key saying why. `LabsLtd/bata-ui#50` C2.
   *
   * A file outlives the room it was downloaded in. The screen carries a
   * `FixtureProvenance` marker beside the panel (`docs/adr/0083`), and that marker is the
   * one thing an export strips: the numbers travel and the sentence next to them does not.
   * So the statement is repeated here, where it cannot be separated from the figures, and
   * `provenanceRows` renders it into the sheet.
   *
   * OPTIONAL, and absent rather than empty when an export is fully measured — a tenth row
   * reading "none" on every fully-measured export is noise that trains a reader to skip
   * the block. `undefined` and `[]` are therefore the same state here, which is the one
   * place in this module where that is true and is why it is said out loud.
   *
   * It is NOT one of G2.4's nine fields and is not added to `PROVENANCE_FIELDS`: those are
   * required and this is a property of an unfinished contract rather than of an export.
   * When the last fixture goes, this field goes with it.
   */
  readonly illustrative?: readonly IllustrativeColumns[];
}

/** What in this sheet is not measured, and why. */
export interface IllustrativeColumns {
  /**
   * Column headers as the sheet spells them, or `'all'` when every row in the body is
   * illustrative.
   *
   * `'all'` is a literal rather than an empty array on purpose. Absence-means-everything is
   * the `0`-versus-`null` confusion this estate keeps repairing: a caller who forgot to fill
   * the list would silently assert the strongest possible claim. As measured on 2026-09-17
   * all ten call sites are `'all'` — each resolver maps over a fixture array to produce the
   * whole body — and the column-subset form exists for the first sheet that is genuinely
   * half-measured rather than being speculative generality.
   */
  readonly columns: readonly string[] | 'all';
  /** ICU key in the chapter's namespace — the same reason the screen shows. */
  readonly reasonKey: string;
}

export const PROVENANCE_FIELDS = [
  'filters', 'hierarchyNode', 'timeWindow', 'scenario', 'planVersionId',
  'measureDefinitions', 'generatedAt', 'generatedBy', 'platformVersion',
] as const satisfies readonly (keyof Provenance)[];

export interface ProvenanceFilter {
  readonly field: string;
  readonly operator: string;
  readonly value: string;
}

export interface MeasureCitation {
  readonly measure: string;
  /** The semantic-layer definition, verbatim. reqs/00 D9 owns it. */
  readonly definition: string;
  /** `metrics-vN`. A metric redefinition changes historical reported numbers and
   *  must be citable in an export's provenance sheet (G7.5). */
  readonly registerVersion: string;
}

// ── the data itself ──────────────────────────────────────────────────────────

/**
 * reqs/00 D8's three missing-data states must survive to the export, not only to
 * the pixel. A store that was not ranged is not a store that sold nothing, and a
 * CSV that renders both as `0` has destroyed the distinction.
 */
export type MissingState = 'counted-zero' | 'not-ranged' | 'not-reported';

export type ExportCell =
  | { readonly kind: 'number'; readonly raw: number }
  | { readonly kind: 'money'; readonly raw: number; readonly currency: string }
  | { readonly kind: 'percent'; readonly raw: number }
  | { readonly kind: 'integer'; readonly raw: number }
  /** ISO-8601 only. G2.4: "ISO-8601 dates … never a display string." */
  | { readonly kind: 'date'; readonly iso: string }
  | { readonly kind: 'text'; readonly value: string }
  /** A reference-data label whose translation is data, not a catalogue key (G6). */
  | { readonly kind: 'label'; readonly value: string; readonly code: string }
  | { readonly kind: 'missing'; readonly state: MissingState };

export type ColumnKind = ExportCell['kind'];

export interface ExportColumn {
  /** Stable machine name. Becomes the CSV header; the workbook falls back to it without a translation. */
  readonly key: string;
  /** ICU key for the human header — the workbook's header, in the user's locale (G6, DEC-104). */
  readonly headerKey: string;
  readonly kind: ColumnKind;
  /** Unit of measure, e.g. `units`, `pairs`, `%`, `weeks`. Shown beside the header
   *  rather than repeated per cell (design/09 §4.3). */
  readonly unit?: string;
  /** Required on a `money` column. */
  readonly currency?: string;
  /**
   * Direction of "good" for this measure. design/09 §5.3: sell-through up is good,
   * markdown up is not, stock cover up is neither without a target. Never
   * hard-coded — the measure declares it and the UI reads it.
   */
  readonly polarity?: Polarity;
  /**
   * ICU key for what the column MEANS, in the reader's language — one or two plain sentences:
   * what is measured, its unit or sign convention, and what an empty cell means where it can be
   * empty (`docs/adr/9045`). Read by the column guide only — the guide file and the workbook's
   * «Columns» sheet — never by the CSV, which does not change.
   *
   * Optional, and a column without one is SAID to have none: the «Columns» sheet writes «Description
   * not yet written» on its row rather than leaving a blank or inventing a meaning (ADR 9045,
   * amended 2026-10-15), and `src/export/columns-census.test.ts` lists every such column.
   */
  readonly descriptionKey?: string;
  /** Values for the ICU placeholders of `descriptionKey`. */
  readonly descriptionValues?: Readonly<Record<string, string | number>>;

  // ── the Excel workbook only (DEC-104, `docs/adr/9041`). The CSV ignores every field below. ──

  /** Values for the ICU placeholders of `headerKey` — a header such as «Net value, {currency}». */
  readonly headerValues?: Readonly<Record<string, string | number>>;
  /**
   * The header itself, when it is DATA rather than copy — a size as the customer names it, which
   * no catalogue holds (G6). Takes precedence over `headerKey` in the workbook.
   */
  readonly header?: string;
  /**
   * A code as the words a person reads, in the workbook only. The CSV keeps the code. Handed the
   * writer's own `translate`, so a column names its catalogue and holds no string (G6).
   */
  readonly display?: (code: string, translate: ExportTranslate) => string;
  /**
   * Decimal places the workbook SHOWS — the cell keeps full precision. Defaults: 0 on an integer,
   * 1 on a number (weeks, ratios), the currency's ISO 4217 digits on money, 1 on a percentage.
   */
  readonly decimals?: number;
  /** A zero is shown as an empty cell — a pivot's empty intersection. The cell still holds 0. */
  readonly zeroAsBlank?: true;
  /**
   * What an ABSENT cell of this column says to a person. Without it a `not-ranged` cell reads
   * «Non assortito» — reqs/00 D8's assortment state, right for a store × article that was never
   * ranged and wrong anywhere else. A column that uses `not-ranged` for "does not apply here" (a
   * pending line's decision time, a driver a line does not carry) says how that reads instead:
   *
   *   `notRanged`   `blank` — legitimately empty, nothing to show; `notAvailable` — «n.d.»;
   *                 `reason` — the cell reads its own `reasonKeys['not-ranged']` words, for a
   *                 column whose emptiness is itself the answer (a simulation's «Adopted on»
   *                 reads «Not adopted», not «Non assortito» and not a blank)
   *   `reasonKeys`  why, per state: written in «Tecnico» beside the row's number, in a column
   *                 `<key>_absent_reason`, so the cell itself stays clean
   *
   * The CSV keeps the state code (`not-ranged`, `not-reported`) unchanged.
   */
  readonly absent?: {
    readonly notRanged?: 'blank' | 'notAvailable' | 'reason';
    readonly reasonKeys?: Readonly<Partial<Record<MissingState, string>>>;
  };
  /** Frozen at the left with the header. Mark the LEADING columns; the workbook freezes up to the last one marked. */
  readonly freeze?: true;
  /** A totals row under the rows: `SUBTOTAL(109, …)`, so it follows the autofilter. */
  readonly total?: 'sum';
  /**
   * Moved to the «Tecnico» sheet, keyed by the row: an id, a code, a reason. A column whose key
   * ends `_absent_reason` is technical without saying so.
   */
  readonly technical?: true;
  /** The row's id: written on the «Tecnico» table beside the row number, whether technical or not. */
  readonly identifies?: true;
  /**
   * The same value on every row — the run's provenance (ADR 9120). The CSV carries it per row; the
   * workbook writes it ONCE, in the «Tecnico» sheet.
   */
  readonly provenance?: true;
}

/** Resolves an ICU key, with its placeholder values. Injected, so the writers hold no strings (G6). */
export type ExportTranslate = (key: string, values?: Readonly<Record<string, unknown>>) => string;

/**
 * One data sheet of the workbook (DEC-104). A request without `workbook` has one: its own title,
 * columns and rows. A request with one writes these sheets INSTEAD — the Excel file is laid out
 * for people while the CSV stays the request's own table, for programs.
 */
export interface WorkbookSheet {
  /** ICU key of the sheet's name — a short title, ≤31 characters once translated. */
  readonly titleKey: string;
  readonly columns: readonly ExportColumn[];
  readonly rows: () => AsyncIterable<readonly ExportCell[]> | Iterable<readonly ExportCell[]>;
}

export const POLARITIES = ['higher-is-better', 'lower-is-better', 'target-relative', 'none'] as const;
export type Polarity = (typeof POLARITIES)[number];

export interface ExportRequest {
  /** Matches the `ExportRegistration.id` that produced it. */
  readonly id: string;
  /** ICU key for the file's human title. */
  readonly titleKey: string;
  readonly columns: readonly ExportColumn[];
  /**
   * Rows in the order the planner is looking at them, honouring the current sort.
   * A generator rather than an array so a 2 M-row extract does not have to be
   * resident — G2.4's async path streams it.
   */
  readonly rows: () => AsyncIterable<readonly ExportCell[]> | Iterable<readonly ExportCell[]>;
  /** Known row count where cheap, for the ≤100k synchronous / >100k async decision. */
  readonly rowCount?: number;
  readonly provenance: Provenance;
  /**
   * The current view, verbatim. G2.4's fidelity rule: the export reflects the
   * current filters, sort, column set and drill level.
   */
  readonly perimeter: Perimeter;
  /** A chart's table view exports on the same path as the chart (G2.4). */
  readonly chart?: ChartExportSource;
  /**
   * The workbook's data sheets when they are not the CSV's table (DEC-104, `docs/adr/9041`):
   * names beside codes, a summary, columns in a person's order. Absent: one sheet, the table.
   */
  readonly workbook?: {
    /** ICU key of the title the workbook's FILE is named after; the request's `titleKey` otherwise. */
    readonly titleKey?: string;
    readonly sheets: readonly WorkbookSheet[];
  };
}

/**
 * What a column guide FILE is built from — the export's columns, and nothing read (`docs/adr/9045`).
 * The guide describes the columns, not the rows, so it is produced without a request to the
 * service and without the rows the export would carry.
 */
export interface ColumnGuideSource {
  /** The export's id, as registered. */
  readonly id: string;
  /** ICU key of the export's human title; the guide file is named after it. */
  readonly titleKey: string;
  /** The export's columns, in the CSV's order. */
  readonly columns: readonly ExportColumn[];
}

export interface Perimeter {
  readonly filters: readonly ProvenanceFilter[];
  readonly sort: readonly { readonly key: string; readonly direction: 'asc' | 'desc' }[];
  readonly visibleColumns: readonly string[];
  readonly drillPath: readonly string[];
  readonly densityMode: string;
}

export interface ChartExportSource {
  /** Returns the rendered chart as an image. Implemented by src/charts/. */
  readonly toImage: (format: 'png' | 'svg') => Promise<Blob>;
}

// ── the boundary between synchronous and asynchronous ────────────────────────

/** G2.1 / G2.4 — "Export up to 100k rows: 10 s synchronous. Above: asynchronous
 *  with progress and notification." */
export const SYNCHRONOUS_ROW_LIMIT = 100_000;

/** G2.4 — CSV and XLSX are mandatory everywhere; PNG and SVG additionally on a chart;
 *  Parquet for system-to-system extracts, produced server-side. `csv-decimal-comma` is the
 *  second CSV dialect, offered wherever the CSV is (owner ruling 2026-10-22, `docs/adr/9072`).
 *  A manifest never declares it: it rides on `csv`. */
export type ExportFormat = 'csv' | 'csv-decimal-comma' | 'xlsx' | 'png' | 'svg' | 'parquet';

/**
 * The two CSV dialects (`docs/adr/9072`): `standard` is G2.4's raw file for systems, with `,`
 * between fields and a decimal point. `decimal-comma` is for a person opening the file in Excel set
 * to Italian or Spanish, with `;` between fields and a decimal comma. Columns, headers, rows, BOM
 * and CRLF are the same.
 */
export type CsvDialect = 'standard' | 'decimal-comma';

/** The file-name suffix of the decimal-comma CSV, so it sits beside the raw one in Downloads. */
export const DECIMAL_COMMA_SUFFIX = '-decimal-comma';

/**
 * The formats a download offers, given the ones it declares: the decimal-comma CSV right after
 * the CSV, wherever a CSV is offered.
 */
export function withDecimalCommaCsv<F extends string>(formats: readonly F[]): (F | 'csv-decimal-comma')[] {
  return formats.flatMap((format): (F | 'csv-decimal-comma')[] => (format === 'csv' ? [format, 'csv-decimal-comma'] : [format]));
}

export type ExportOutcome =
  | { readonly mode: 'synchronous'; readonly blob: Blob; readonly filename: string }
  | {
    readonly mode: 'asynchronous';
    /** 202 + a job resource, per G12. Never a held connection. */
    readonly jobId: string;
    /** Download link expiry, ISO-8601 (G2.4 "with an expiry"). */
    readonly expiresAt: string;
  };

/**
 * G2.4 governance — "Exports are RBAC-scoped to the requester's row-level
 * permissions and written to the audit log: who exported what, when, at what
 * perimeter." The client emits the intent; the server writes the record.
 */
export interface ExportAuditEvent {
  readonly exportId: string;
  readonly principalId: string;
  readonly requestedAt: string;
  readonly format: string;
  readonly rowCount: number | null;
  readonly perimeter: Perimeter;
}
